<template>
  <div v-if="imageList" class="banner" :style="background" @click="() => goToLink(slide.buttonAction)">
    <picture class="banner__background" :style="background">
      <source media="(min-width:1100px)" :srcset="getImage(slide.imageBackground, 'lg', true)" />
      <source media="(max-width:430px)" :srcset="getImage(slide.imageBackground, 'sm', true)" />
      <source
        media="(max-width:860px)"
        :srcset="`${getUrlWithModifier(getImage(slide.imageBackground, 'sm'), 2)} 1x, ${getUrlWithModifier(
          getImage(slide.imageBackground, 'sm'),
          3
        )} 2x`"
      />
      <source media="(max-width:1099px)" :srcset="getUrlWithModifier(getImage(slide.imageBackground, 'sm'), 3)" />
      <img loading="eager" :src="getUrlWithModifier(getImage(slide.imageBackground, 'sm'), 3)" alt="" />
    </picture>
    <picture class="banner__image">
      <source media="(min-width:786px)" :srcset="getImage(slide.image, 'lg', true)" />
      <source media="(max-width:767px)" :srcset="getImage(slide.image, 'sm', true)" />
      <img loading="eager" :src="getUrlWithModifier(getImage(slide.image, 'sm'), 3)" alt="" />
    </picture>
    <div class="banner__header">
      <div class="banner__type">{{ getLocaleText(slide.category) }}</div>
      <div class="banner__date">{{ bannerDate }}</div>
      <picture class="banner__logo">
        <!--        <source media="(min-width:1100px)" :srcset="getImage(slide.imageTop, 'md', true)" />-->
        <!--        <source media="(max-width:1099px)" :srcset="getImage(slide.imageTop, 'md', true)" />-->
        <img loading="eager" :src="getUrlWithModifier(getImage(slide.imageTop, 'md'), 2)" alt="" />
      </picture>
    </div>
    <div class="banner__body">
      <div class="banner__title">{{ getLocaleText(slide.title) }}</div>
      <div class="banner__text">{{ getLocaleText(slide.description) }}</div>
    </div>
    <div class="banner__footer">
      <div class="banner__text">{{ getLocaleText(slide.additionalDescription) }}</div>
      <div
        v-if="getLocaleText(slide.promocode) || valueByCurrencies"
        class="banner__title text-yellow-8"
        v-html="getLocaleText(slide.promocode) || valueByCurrencies"
      />
      <ol-button
        v-if="getLocaleText(slide.button)"
        class="banner__button"
        size="sm"
        color="gray-night-100"
        :label="getLocaleText(slide.button)"
      />

      <div v-if="currentCoefficients" class="banner__footer-coefficients">
        <div v-for="(coefficient, index) in currentCoefficients" :key="index" class="banner__coefficient">
          <template v-if="slide?.coefficientsType === '1'">
            <span v-if="index === 2" class="banner__coefficient-label">2</span>
            <span v-else class="banner__coefficient-label">{{ index === 1 ? 'X' : index + 1 }}</span>
            <span class="banner__coefficient-value">{{ coefficient.k }}</span>
          </template>
          <template v-else>
            <span class="banner__coefficient-label">{{ index + 1 }}</span>
            <span class="banner__coefficient-value">{{ coefficient.k }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getCompleteSrcset, getUrlWithModifier, numberFilter } from 'src/common/functions'
import { externalLinkMessageToApp } from 'src/common/webview'
import { OlButton } from '@gamesite/olimp-core'
import { format } from 'date-fns'
import { az, uz } from 'date-fns/locale'
import { useSiteInfoStore } from 'src/stores/site-info'

const props = defineProps({
  name: {
    type: Number,
    default: null,
  },
  first: {
    type: Boolean,
    default: false,
  },
  slide: {
    type: Object,
    default: null,
  },
  imageList: {
    type: Object,
    default: () => {},
  },
  coefficientsData: {
    type: Array,
    default: () => [],
  },
})
const router = useRouter()
const lang = useI18n().locale
const getCurrencyCode = computed(() => useSiteInfoStore().getCurrencyCode)
const background = computed(() => {
  return { background: `linear-gradient(to top, ${props.slide?.gradientStart}, ${props.slide?.gradientEnd})` }
})
const bannerDate = computed(() => {
  const dateStart = props.slide.dateStart ? getDate(new Date(props.slide?.dateStart)) : ''
  const dateEnd = props.slide.dateEnd ? getDate(new Date(props.slide?.dateEnd)) : ''
  return `${dateStart}${dateStart && dateEnd ? ' – ' : ''}${dateEnd}`
})
const valueByCurrencies = computed(() => {
  const currency = getCurrencyCode.value
  return props.slide?.currency[currency] && numberFilter(props.slide?.currency[currency], currency, 0, 2)
})

const currentCoefficients = computed(() => {
  const coefficientsByType = props.coefficientsData.find((data) => {
    return data.type === props.slide?.coefficientsType
  })
  if (!coefficientsByType) {
    return props.coefficientsData[0]?.coefficients
  }
  return coefficientsByType?.coefficients
})

function goToLink(link) {
  if (link.startsWith('http')) {
    if (useSiteInfoStore().isWebView) {
      return externalLinkMessageToApp(link)
    }
    return window.open(link, '_blank')
  }
  router.push(link).catch(() => {})
}

function getImage(image, size, srcset = false) {
  let id = 0

  // Раньше в пропсах картинок хранился объект, теперь будет храниться id. Сделано для обратной совместимости
  if (typeof image === 'object') {
    id = image?.id
  } else if (typeof image === 'number') {
    id = image
  } else {
    return
  }

  const objectImage = props.imageList[id]

  if (objectImage?.compressedImage) {
    return srcset ? getCompleteSrcset(objectImage.compressedImage?.webp[size]) : objectImage.compressedImage?.webp[size]
  }
}

function getDate(date) {
  // Не поддерживается локаль az и uz
  if (lang.value === 'az') {
    return format(date, 'dd MMM', { locale: az })
  }
  if (lang.value === 'uz') {
    return format(date, 'dd MMM', { locale: uz })
  }

  return new Intl.DateTimeFormat(lang.value, { day: '2-digit', month: 'short' }).format(date)
}

function getLocaleText(locale) {
  if (locale) {
    return locale[lang.value] ? locale[lang.value] : locale.ru
  }
}
</script>

<style lang="scss" scoped>
.banner {
  --imageWidth: 150px;
  color: var(--cas-light-900);
  border-radius: var(--radius-2);
  padding: 0 0 8px 16px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  position: relative;
  //max-width: 85%;
  height: 180px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (min-width: 768px) {
    padding: 0 0 16px 32px;
    font-size: 16px;
    //max-width: 70%;
    --imageWidth: 60%;
    height: 275px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    gap: 8px;
    font-size: 12px;
    height: 44px;
    position: relative;
    z-index: 2;
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }

  &__type {
    font-weight: 500;
    line-height: 12px;
    text-transform: uppercase;
  }

  &__date {
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    font-size: 12px;
    white-space: nowrap;
  }

  &__logo {
    margin-left: auto;
    display: flex;
    align-items: center;
    width: auto;
    max-height: 20px;
    height: 100%;
    margin-right: 20px;

    img {
      object-position: right;
    }

    @media (min-width: 768px) {
      margin-right: 36px;
    }
  }

  &__background {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    & img {
      object-fit: cover;
      object-position: center;
    }
  }

  &__image {
    display: flex;
    position: absolute;
    right: 0;
    bottom: 0;
    top: 35px;
    width: var(--imageWidth);
    @media (min-width: 1100px) {
      padding: 0 16px;
      //width: 340px;
    }

    img {
      object-fit: contain;
      object-position: bottom;
      width: 100%;
    }
  }

  &__body {
    padding-right: var(--imageWidth);
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 2;
    @media (min-width: 768px) {
      gap: 12px;
    }
  }

  &__title {
    color: #fff;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 1;
    overflow: hidden;
    display: -webkit-box;
    pointer-events: auto;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    @media (min-width: 768px) {
      font-size: 32px;
    }
  }

  &__text {
    display: -webkit-box;
    line-height: 1.1;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__footer {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    position: relative;
    z-index: 3;
  }

  &__button {
    display: none;
    position: relative;
    z-index: 10;
    @media (min-width: 1100px) {
      display: block;
    }
  }
  &__footer-coefficients {
    width: 100%;
    max-width: 200px;
    display: flex;
    gap: 4px;
    margin-top: 8px;

    @include tab {
      max-width: 320px;
      margin-top: 20px;
    }
    @include desktop {
      max-width: 400px;
    }
  }

  &__coefficient {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    height: 32px;
    flex: 1 1;
    border: 1px solid var(--cas-light-100);
    border-radius: var(--border-radius-2);
    position: relative;
    backdrop-filter: blur(32px);
    background: var(--cas-light-200);
    transition: 0.3s ease;
    color: var(--cas-light-900);
    font-weight: 500;
    font-size: 12px;
    line-height: 12px;
    @include tab {
      height: 28px;
    }
    @include desktop {
      cursor: pointer;
      transition: 0.16s;
      &:hover {
        border: 1px solid var(--cas-light-300);
        background: var(--cas-light-300);
      }
    }
  }

  &__coefficient-label {
    color: var(--cas-light-500);
  }
}
</style>
